import { useState, useCallback, useRef, useEffect } from 'react'
import { buildLangUrl } from '@fs/zion-router'

/**
 * Hook to ask a single question to the genealogist AI with no conversational context.
 * Provides automatic cancellation of inflight requests when new requests are made
 * or when the component unmounts, with streaming support.
 * @returns {{
 * loading: boolean,
 * answer: string | null,
 * error: Error | null,
 * streamingContent: string,
 * isStreaming: boolean,
 * askQuestion: (messages: Array<Object>) => Promise<void>
 * }}
 */
export default function useGenealogistQuestion() {
  const [loading, setLoading] = useState(false)
  const [answer, setAnswer] = useState(null)
  const [error, setError] = useState(null)
  const [streamingContent, setStreamingContent] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const abortControllerRef = useRef(null)

  // Helper function to reset all state for a new request
  const resetState = useCallback(() => {
    setLoading(true)
    setAnswer(null)
    setError(null)
    setIsStreaming(true)
    setStreamingContent('')
  }, [])

  // Helper function to set final success state
  const setSuccessState = useCallback((finalResponse) => {
    setAnswer(finalResponse.trim())
    setLoading(false)
    setIsStreaming(false)
    setStreamingContent('')
  }, [])

  // Helper function to set error state
  const setErrorState = useCallback((err, signal) => {
    // Don't set error state if the request was aborted
    if (err.name !== 'AbortError' && !signal.aborted) {
      setError(err)
    }
    // Always reset loading state regardless of error type
    setLoading(false)
    setIsStreaming(false)
    setStreamingContent('')
  }, [])

  // Helper function to setup abort controller
  const setupAbortController = useCallback(() => {
    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController()
    return abortControllerRef.current.signal
  }, [])

  // Helper function to make the API request
  const makeRequest = useCallback(async (messages, signal) => {
    const langUrl = buildLangUrl('records/improve/azureAiPrompt41')

    const response = await fetch(langUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ messages }),
      signal,
    })

    if (signal.aborted) {
      return null
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response
  }, [])

  // Helper function to process a single chunk
  const processChunk = useCallback((value, decoder, done, fullResponse) => {
    if (!value) {
      return fullResponse
    }

    // Use stream: false on the final chunk to flush any remaining bytes
    const chunkText = decoder.decode(value, { stream: !done })
    const newFullResponse = fullResponse + chunkText
    setStreamingContent(newFullResponse)
    return newFullResponse
  }, [])

  // Helper function to read the stream
  const readStream = useCallback(
    async (response, signal) => {
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullResponse = ''

      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read()

          // Check if aborted during streaming
          if (signal.aborted) {
            reader.cancel()
            return null
          }

          fullResponse = processChunk(value, decoder, done, fullResponse)

          if (done) break
        }

        // Flush any remaining bytes from the decoder
        const finalChunk = decoder.decode()
        if (finalChunk) {
          fullResponse += finalChunk
          setStreamingContent(fullResponse)
        }

        return signal.aborted ? null : fullResponse
      } finally {
        reader.releaseLock()
      }
    },
    [processChunk]
  )

  const askQuestion = useCallback(
    async (messages) => {
      const signal = setupAbortController()
      resetState()

      try {
        const response = await makeRequest(messages, signal)
        if (!response) return // Request was aborted

        const fullResponse = await readStream(response, signal)
        if (fullResponse === null) return // Request was aborted

        setSuccessState(fullResponse)
      } catch (err) {
        setErrorState(err, signal)
      }
    },
    [setupAbortController, resetState, makeRequest, readStream, setSuccessState, setErrorState]
  )

  // Cleanup effect to cancel any pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
        abortControllerRef.current = null
      }
    }
  }, [])

  return {
    loading,
    answer,
    error,
    streamingContent,
    isStreaming,
    askQuestion,
  }
}
